import { ColorSource, Container as PixiContainer, Graphics as PixiGraphics, Rectangle } from 'pixi.js'
import { applyDefaultProps, Container, PixiComponent, Graphics } from '@pixi/react'
import { ComponentProps, forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
export class FixedSizeContainerBase extends PixiContainer {
  private fixedWidth: number
  private fixedHeight: number
  private hasMask: boolean

  constructor(width: number, height: number, hasMask = true) {
    super()
    this.fixedWidth = width
    this.fixedHeight = height
    this.hasMask = hasMask

    if (this.hasMask) {
      const g = new PixiGraphics()
      g.name = 'Mask'
      g.beginFill(0xffffff)
      g.drawRect(0, 0, width, height)
      g.endFill()
      this.addChild(g)
      this.mask = g
    }
  }

  getLocalBounds(rect: Rectangle) {
    if (!rect) {
      rect = new Rectangle()
    }

    rect.width = this.fixedWidth
    rect.height = this.fixedHeight

    return rect
  }

  update(x: number, y: number, width: number, height: number) {
    this.x = x
    this.y = y
    this.fixedWidth = width
    this.fixedHeight = height

    if (this.hasMask) {
      const g = this.mask as PixiGraphics
      g.clear()
      g.beginFill(0xffffff)
      g.drawRect(0, 0, width, height)
      g.endFill()
    }
  }
}

interface IFixedContainer extends ComponentProps<typeof Container> {
  width: number
  height: number
  hasMask?: boolean
}

/**
 * @deprecated This component is deprecated. Use FixedContainerV2() instead.
 */
export const FixedContainer = PixiComponent<IFixedContainer, FixedSizeContainerBase>('FixedContainer', {
  create: (props) => {
    const container = new FixedSizeContainerBase(props.width, props.height, props.hasMask)
    container.position.set(props.x, props.y)
    if (props.name !== undefined) container.name = props.name
    return container
  },
  applyProps(
    instance: FixedSizeContainerBase,
    oldProps: Readonly<IFixedContainer>,
    newProps: Readonly<IFixedContainer>,
  ) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    instance.update(newProps.x, newProps.y, newProps.width, newProps.height)
    applyDefaultProps(instance, oldProps, newProps)
  },
})

type FixedContainerV2Props = ComponentProps<typeof Container> & {
  background?: ColorSource
  backgroundAlpha?: number
  blockEvents?: boolean
  width: number
  height: number
}

export const FixedContainerV2 = forwardRef<PixiContainer, FixedContainerV2Props>(
  ({ name, width, height, background, backgroundAlpha, blockEvents = false, ...props }: FixedContainerV2Props, ref) => {
    const containerRef = useRef<PixiContainer>(null)
    const graphicsRef = useRef<PixiGraphics>(null)

    useEffect(() => {
      if (containerRef.current && graphicsRef.current) {
        containerRef.current.mask = graphicsRef.current
      }
    }, [])
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    useImperativeHandle(ref, () => containerRef.current)

    return (
      <Container
        {...props}
        name={name ?? 'FixedContainerV2'}
        ref={containerRef}
      >
        <Graphics
          hitArea={blockEvents ? new Rectangle(0, 0, width, height) : null}
          draw={(graphics) => {
            graphics.clear()
            graphics.beginFill(background === undefined ? 0x000000 : background, backgroundAlpha ?? 1)
            if (background === undefined) graphics.alpha = 0
            graphics.drawRect(0, 0, width, height)
            graphics.endFill()
            if (!blockEvents) graphics.eventMode = 'none'
          }}
        />
        {props.children}
        <Graphics
          ref={graphicsRef}
          draw={(graphics) => {
            graphics.clear()
            graphics.beginFill(0x000000, 0.00001)
            graphics.drawRect(0, 0, width, height)
            graphics.endFill()
          }}
        />
      </Container>
    )
  },
)
